<template>
  <div class="rich-text-field form-control h-auto" v-if="editor">
    <editor-menu-bubble :editor="editor" keep-in-bounds v-slot="{ commands, isActive, menu }">
      <div class="menububble bg-secondary" :class="{ 'is-active': menu.isActive }" :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`">
        <button class="menububble__button" :class="{ 'is-active': isActive.bold() }" @click.prevent="commands.bold">
          <font-awesome-icon icon="bold"/>
        </button>

        <button class="menububble__button" :class="{ 'is-active': isActive.italic() }" @click.prevent="commands.italic">
          <font-awesome-icon icon="italic"/>
        </button>

        <button class="menububble__button" :class="{ 'is-active': isActive.strike() }" @click.prevent="commands.strike">
          <font-awesome-icon icon="strikethrough"/>
        </button>
      </div>
    </editor-menu-bubble>

    <editor-floating-menu :editor="editor" v-slot="{ commands, isActive, menu }">
      <div
        class="floating-menu"
        :class="{ 'is-active': menu.isActive }"
        :style="`top: ${menu.top}px`"
      >

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click.prevent="commands.heading({ level: 1 })"
        >
          H1
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click.prevent="commands.heading({ level: 2 })"
        >
          H2
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click.prevent="commands.heading({ level: 3 })"
        >
          H3
        </button>

        <!-- <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click.prevent="commands.bullet_list"
        >
          —
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click.prevent="commands.ordered_list"
        >
          1.
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.blockquote() }"
          @click.prevent="commands.blockquote"
        >
          "
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.code_block() }"
          @click.prevent="commands.code_block"
        >
          Code
        </button> -->

        <button
          class="menubar__button"
          @click.prevent="promptForImage(commands.image)"
        >
          IMG
        </button>

        <button
          class="menubar__button"
          @click.prevent="promptForImage(commands.iframe)"
        >
          EMBED
        </button>

        <!-- <button
          class="menubar__button"
          @click.prevent="promptForLink(commands.link, getMarkAttrs('link'))" :pressed="isActive.link()">
            <font-awesome-icon icon="link"/>
        </button> -->

      </div>
    </editor-floating-menu>

    <editor-content class="rich-text-content" :editor="editor" @click="() => editor.focus()"/>

    <b-btn class="help-button" id="help-tooltip" size="sm" variant="transparent">?</b-btn>
    <b-tooltip target="help-tooltip" triggers="hover" placement="left">
      Select text for formatting options, insert a new line for block options
    </b-tooltip>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBubble, EditorFloatingMenu, Node } from 'tiptap';
import {
  Bold, Italic, Strike, Link, Heading,
  Image, Placeholder, ListItem, OrderedList, TrailingNode, HardBreak
} from 'tiptap-extensions';

export default {
  props: {
    extensions: {
      type: Array,
      default: () => [ 'bold', 'italic', 'strike', 'link', 'ul', 'ol' ],
    },
    placeholder: String,
    value: String,
  },
  components: {
    EditorContent,
    EditorMenuBubble,
    EditorFloatingMenu,
  },
  data: () => ({
    editor: null,
  }),
  created() {
    const extensions = [
      new Bold(),
      new Italic(),
      new Strike(),
      new Link(),
      new TrailingNode({ node: 'paragraph', notAfter: [ 'paragraph' ] }),
      new ListItem(),
      new OrderedList(),
      new Heading({ levels: [ 1, 2, 3, 4 ] }),
      new Image(),
      new HtmlIframe(),
      new HardBreak()
    ];

    if (this.placeholder) {
      extensions.push(new Placeholder({
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-empty',
        emptyNodeText: this.placeholder,
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
      }));
    }

    this.editor = new Editor({
      disablePasteRules: true,
      extensions,
      content: this.value,
      onUpdate: ({ getHTML }) => this.$emit('input', cleanInput.call(this, getHTML())),
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    async promptForImage(command, { src } = {}) {
      if (src) {
        command({ src: null });
      } else {
        try {
          const { $createElement: el } = this;
          const input = el('b-form-input', {
            props: { placeholder: 'https://...', value: src },
            on: { input: value => src = value },
          });

          await this.$bvModal.msgBoxConfirm([ input ], {
            title: 'Insert Link',
            footerClass: 'border-0 pt-0',
            okTitle: 'Save',
          });

          this.$assert(src, 'Expected URL to be set');
          command({ src });
        } catch (err) {
          this.$notifyErr(err);
        }
      }
    },
    async promptForLink(command, { href } = {}) {
      if (href) {
        command({ href: null });
      } else {
        try {
          const { $createElement: el } = this;
          const input = el('b-form-input', {
            props: { placeholder: 'https://...', value: href },
            on: { input: value => href = value },
          });

          await this.$bvModal.msgBoxConfirm([ input ], {
            title: 'Insert Link',
            footerClass: 'border-0 pt-0',
            okTitle: 'Save',
          });

          this.$assert(href, 'Expected URL to be set');
          command({ href });
        } catch (err) {
          this.$notifyErr(err);
        }
      }
    },
  },
};

export const RichTextContent = {
  props: {
    value: String,
  },
  render(el) {
    return el('div', { class: 'rich-text-content', domProps: { innerHTML: this.value } });
  },
  mounted() {
    try {
      Array.from(this.$el.children).filter(({ nodeName }) => nodeName === 'IFRAME').forEach(iframe => {
        const newiFrame = document.createElement('iframe');
        newiFrame.setAttribute('src', iframe.getAttribute('src'));

        const wrapped = document.createElement('div');
        wrapped.className = 'embed-responsive embed-responsive-16by9';
        wrapped.setAttribute('data-src', iframe.getAttribute('src'));
        wrapped.appendChild(newiFrame);

        iframe.parentNode.replaceChild(wrapped, iframe);
      });
    } catch (err) {
      this.$log.error(err, 'Failed to replace iframes');
    }
  },
};

function cleanInput(input) {
  return input.trim().replace(/<p><\/p>$/, '').replace(/\\n/g, ' ').replace(/ +(?= )/g, '') || null;
}

export class HtmlIframe extends Node {
  get name() {
    return 'iframe';
  }
  get schema() {
    return {
      group: 'block',
      attrs: { src: { default: null } },
      selectable: false,
      // parseDOM: [ { tag: 'iframe', getAttrs: dom => ({ src: dom.getAttribute('src') }) } ],
      parseDOM: [
        { tag: 'iframe', getAttrs: dom => ({ src: dom.getAttribute('src') }) },
        {
          tag: 'div',
          attrs: { class: 'embed-responsive' },
          getAttrs(dom) {
            try {
              const iframe = dom.getElementsByTagName('iframe');
              return { src: iframe && iframe[0] ? iframe[0].getAttribute('src') : null };
            } catch (err) {
              console.error(err);
              return {};
            }
          },
        },
      ],
      toDOM: node => [ 'iframe', { src: node.attrs.src, frameborder: 0, allowfullscreen: 'true' } ],
    };
  }
  commands({ type }) {
    return attrs => (state, dispatch) => {
      const { selection } = state;
      const position = selection.$cursor ? selection.$cursor.pos : selection.$to.pos;
      const node = type.create(attrs);
      const transaction = state.tr.insert(position, node);
      dispatch(transaction);
    };
  }
  get view() {
    return {
      props: [ 'node', 'updateAttrs', 'view' ],
      computed: {
        src: {
          get() {
            return this.node.attrs.src;
          },
          set(src) {
            this.updateAttrs({ src });
          },
        },
      },
      render(el) {
        return el('div', { class: 'embed-responsive embed-responsive-16by9' }, [
          el('iframe', { attrs: { src: this.src } }),
        ]);
      },
    };
  }
}
</script>

<style lang="scss">
.rich-text-field {
  position: relative;
  padding-bottom: 30px;

  .rich-text-content {
    // NOTE See ../scss/main.scss for the global .rich-text-content class used when displaying this content
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
  }

  .floating-menu {
    position: absolute;
    z-index: 1;
    margin-top: -0.25rem;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.2s, visibility 0.2s;

    &.is-active {
      opacity: 1;
      visibility: visible;
    }

    .menubar__button {
      font-weight: bold;
      display: inline-flex;
      background: transparent;
      border: 0;
      color: #000;
      padding: 0.2rem 0.5rem;
      margin-right: 0.2rem;
      border-radius: 3px;
      cursor: pointer;

      &:hover {
        background-color: rgba(#000, 0.05);
      }

      &.is-active {
        background-color: rgba(#000, 0.1);
      }
    }
  }

  .menububble {
    position: absolute;
    display: flex;
    z-index: 20;
    background: #000;
    border-radius: 5px;
    padding: 0.3rem;
    margin-bottom: 0.5rem;
    transform: translateX(-50%);
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.2s, visibility 0.2s;

    &.is-active {
      opacity: 1;
      visibility: visible;
    }

    &__button {
      display: inline-flex;
      background: transparent;
      border: 0;
      color: #fff;
      padding: 0.2rem 0.5rem;
      margin-right: 0.2rem;
      border-radius: 3px;
      cursor: pointer;

      &:last-child {
        margin-right: 0;
      }

      &:hover {
        background-color: rgba(#fff, 0.1);
      }

      &.is-active {
        background-color: rgba(#fff, 0.2);
      }
    }

    &__form {
      display: flex;
      align-items: center;
    }

    &__input {
      font: inherit;
      border: none;
      background: transparent;
      color: #fff;
    }
  }

  p:last-child {
    margin-bottom: 0;
  }

  .ProseMirror {
    outline: none;
    &:focus { outline: none; }

    p {
      font-size: inherit;
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-empty-text);
      float: left;
      color: #aaa;
      pointer-events: none;
      height: 0;
    }
  }

  .help-button {
    position: absolute;
    bottom: 0;
    right: 0;
  }
}

.rich-text-content {
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  img {
    max-height: 500px;
    max-width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/RichTextField.vue